// Full-screen viewer for image attachments — opened from an image bubble and
// shared by both sides of the thread.
//
// Attachment URLs are presigned so <img src> works directly; the Download
// button still routes through the authenticated re-signing endpoint because a
// presigned URL that has been sitting in an open thread may already be stale.

import React, { useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getFileURL } from '../../utils/fileURL';

const ChatImageLightbox = ({ images, index, onIndexChange, onClose, onDownload, testPrefix }) => {
    const { t } = useTranslation(['chat']);
    const count = images?.length || 0;

    useEffect(() => {
        if (!count) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (count > 1 && e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
            if (count > 1 && e.key === 'ArrowRight') onIndexChange((index + 1) % count);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [count, index, onClose, onIndexChange]);

    if (!count) return null;
    const current = images[index] || images[0];

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
            onClick={onClose}
            data-testid={`${testPrefix}-image-preview-modal`}
        >
            <div
                className="relative flex justify-center items-center max-w-[90vw] max-h-[85vh]"
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testPrefix}-image-preview-content`}
            >
                <div className="absolute -top-14 right-0 flex items-center gap-3 z-[101]">
                    <button
                        type="button"
                        onClick={() => onDownload(current.url, current.fileName)}
                        className="p-2.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
                        title={t('chat.downloadImage', 'Download image')}
                        aria-label={t('chat.downloadImage', 'Download image')}
                        data-testid={`${testPrefix}-image-preview-download-button`}
                    >
                        <Download size={19} />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all cursor-pointer"
                        title={t('chat.closePreview', 'Close preview')}
                        aria-label={t('chat.closePreview', 'Close preview')}
                        data-testid={`${testPrefix}-image-preview-close-button`}
                    >
                        <X size={19} />
                    </button>
                </div>

                {count > 1 && (
                    <button
                        type="button"
                        onClick={() => onIndexChange((index - 1 + count) % count)}
                        className="absolute left-2 sm:-left-16 top-1/2 -translate-y-1/2 p-2 sm:p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-[101] cursor-pointer"
                        aria-label={t('chat.previousImage', 'Previous image')}
                        data-testid={`${testPrefix}-image-preview-prev-button`}
                    >
                        <ChevronLeft size={26} />
                    </button>
                )}

                {count > 1 && (
                    <button
                        type="button"
                        onClick={() => onIndexChange((index + 1) % count)}
                        className="absolute right-2 sm:-right-16 top-1/2 -translate-y-1/2 p-2 sm:p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-[101] cursor-pointer"
                        aria-label={t('chat.nextImage', 'Next image')}
                        data-testid={`${testPrefix}-image-preview-next-button`}
                    >
                        <ChevronRight size={26} />
                    </button>
                )}

                <img
                    src={getFileURL(current.url)}
                    alt={current.fileName || t('chat.preview', 'Preview')}
                    className="w-auto h-auto max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                    data-testid={`${testPrefix}-image-preview`}
                />

                {count > 1 && (
                    <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 text-white/90 bg-white/10 px-4 py-1.5 rounded-full text-sm font-medium" data-testid={`${testPrefix}-image-preview-counter`}>
                        {index + 1} / {count}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatImageLightbox;
